import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Button, Container, Grid, Card, CardContent, Avatar, Box, Link as MuiLink, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Chip, IconButton, useMediaQuery, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Drawer, Alert } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import { MessageCircle, Clock, BarChart2, BrainCircuit, Rocket, ShoppingBag, Code, FileText, Users, Server, Shield, Lock, Zap, Database, Settings, Cpu, Cloud, Layers, Network, Bot, Search, Check, ChevronLeft, ChevronRight, Play, Monitor, Wifi, X } from 'lucide-react';
import { useKeycloak } from '@react-keycloak/web';
import PageLoading from '../helpers/PageLoading';
import { landingPageContent } from '../landingpage/landingPageContent';
import { useThemeContext } from '../helpers/ThemeContext';

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.common.black,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '16px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: theme.palette.primary.main,
    boxShadow: theme.palette.mode === 'dark' 
      ? `0 12px 24px ${theme.palette.primary.main}25`
      : `0 12px 24px ${theme.palette.primary.main}25`
  }
}));

const UseCaseCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.background.elevation1,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '16px',
  padding: theme.spacing(3),
  height: '100%',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 8px 16px ${alpha(theme.palette.primary.light, 0.1)}`
  }
}));

const HeroSection = ({ content, keycloak }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Box sx={{ 
      position: 'relative', 
      minHeight: '100vh',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      overflow: 'hidden',
      background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
    }}>
      
      <Container maxWidth="lg" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <Box sx={{ mb: 4 }}>
          <Chip 
            label={content.hero.badge}
            sx={{ 
                background: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              fontWeight: 600,
              px: 2,
              py: 0.5,
              fontSize: '0.875rem'
            }}
            variant="outlined"
          />
        </Box>
        
        <GradientText 
          variant="h1" 
          component="h1" 
          sx={{ 
            mb: 3,
            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
            fontWeight: 800,
            lineHeight: 1.1
          }}
        >
          {content.hero.title}
        </GradientText>
        
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 4,
            color: 'text.secondary',
            fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
            maxWidth: '800px',
            mx: 'auto'
          }}
        >
          {content.hero.subtitle}
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2} justifyContent="center">
            {content.hero.features.map((feature, index) => (
              <Grid item key={index}>
                <Chip 
                  icon={<Check size={16} />}
                  label={feature}
                  sx={{ 
                    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                    color: theme.palette.primary.main,
                    fontWeight: 500
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <StyledButton 
            variant="contained" 
            size="large"
            onClick={() => {
              if (keycloak && !keycloak.authenticated) {
                keycloak.login();
              } else if (keycloak && keycloak.authenticated) {
                window.location.href = '/home';
              }
            }}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600
            }}
          >
            {content.hero.primaryCta}
          </StyledButton>
        </Box>
      </Container>
    </Box>
  );
};


const SolutionsSection = ({ content }) => {
  const theme = useTheme();
  
  return (
    <Box id="solutions" sx={{ py: 12, bgcolor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 700 }}>
          {content.solutions.title}
        </Typography>
        <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 8 }}>
          {content.solutions.subtitle}
        </Typography>
        
        <Grid container spacing={4}>
          {content.solutions.cards.map((card, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <FeatureCard>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {card.description}
                  </Typography>
                  <List>
                    {card.benefits.map((benefit, idx) => (
                      <ListItem key={idx} sx={{ px: 0 }}>
                        <ListItemIcon>
                          <Check size={20} style={{ color: theme.palette.primary.main }} />
                        </ListItemIcon>
                        <ListItemText primary={benefit} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};


// const VideoSection = ({ content }) => {
//   const theme = useTheme();
//   
//   return (
//     <Box id="video" sx={{ py: 12, bgcolor: 'background.paper' }}>
//       <Container maxWidth="lg">
//         <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 700 }}>
//           {content.videoSection.title}
//         </Typography>
//         <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 6 }}>
//           {content.videoSection.subtitle}
//         </Typography>
//         
//         <Box sx={{
//           position: 'relative',
//           paddingTop: '56.25%',
//           bgcolor: 'background.elevation1',
//           borderRadius: 4,
//           overflow: 'hidden',
//           border: `1px solid ${theme.palette.divider}`
//         }}>
//           <Box sx={{
//             position: 'absolute',
//             top: 0,
//             left: 0,
//             right: 0,
//             bottom: 0,
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center'
//           }}>
//             <Typography variant="h4" color="text.secondary">
//               {content.videoSection.placeholder}
//             </Typography>
//           </Box>
//         </Box>
//       </Container>
//     </Box>
//   );
// };



export default function PioSphereLandingPage({ language = 'en', toggleLanguage }) {
  const { keycloak, initialized } = useKeycloak();
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeContext();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const content = landingPageContent[language];

  const handleDrawerToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavClick = (key) => {
    setMobileMenuOpen(false);
    if (key === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const targetId = key === 'solutions' ? 'solutions' : 
                      key === 'features' ? 'features' : null;
      if (targetId) {
        const element = document.getElementById(targetId);
        if (element) {
          const offset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    }
  };

  if (!initialized) {
    return <PageLoading />;
  }

  if (keycloak.authenticated) {
    return <Navigate to="/home" replace />;
  }

  return (
    <Box sx={{ 
      width: '100vw',
      margin: 0,
      padding: 0,
      overflowX: 'hidden',
      bgcolor: 'background.default'
    }}>
      <AppBar 
        position="fixed" 
        elevation={0} 
        sx={{ 
          backgroundColor: alpha(theme.palette.background[theme.palette.mode === 'dark' ? 'default' : 'paper'], 0.9),
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          top: 0,
          zIndex: 1300
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 1 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 }
                }}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <img 
                  key={mode}
                  src="/WhiteLabelLogos/amax-fav.png" 
                  alt="AMAX Logo" 
                  style={{ 
                    height: '40px',
                    filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'none'
                  }} 
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                  <Typography 
                    variant="body1" 
                    component="div" 
                    sx={{ 
                      display: { xs: 'none', sm: 'block' },
                      fontWeight: 600,
                      lineHeight: 1.2,
                      fontSize: '0.95rem'
                    }}
                  >
                    AMAX
                  </Typography>
                  <Typography 
                    variant="caption" 
                    component="div" 
                    sx={{ 
                      fontSize: '0.65rem',
                      color: 'text.secondary',
                      fontWeight: 400,
                      lineHeight: 1.2
                    }}
                  >
                    powered by piosphere
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 3, alignItems: 'center' }}>
              <MuiLink 
                href="#features"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('features');
                }}
                color="inherit" 
                sx={{ 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  fontWeight: 500,
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {content.nav.features}
              </MuiLink>
              <MuiLink 
                href="#solutions"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('solutions');
                }}
                color="inherit" 
                sx={{ 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  fontWeight: 500,
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {content.nav.solutions}
              </MuiLink>
              <MuiLink 
                component={Link}
                to="/documentation"
                color="inherit" 
                sx={{ 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                <FileText size={16} />
                Documentation
              </MuiLink>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MuiLink 
                component={Link}
                to="/documentation"
                sx={{ 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'text.secondary',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                <FileText size={16} />
                <Typography 
                  component="span"
                  sx={{ 
                    fontWeight: 400,
                    fontSize: '0.875rem'
                  }}
                >
                  docs
                </Typography>
              </MuiLink>
              <Button 
                variant="outlined" 
                onClick={() => keycloak.login()}
                sx={{ 
                  color: 'primary.main', 
                  borderColor: 'primary.main',
                  fontWeight: 500,
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                {content.auth.login}
              </Button>
              
              <StyledButton 
                variant="contained"
                onClick={() => {
                  if (keycloak && !keycloak.authenticated) {
                    keycloak.login();
                  }
                }}
                sx={{ 
                  fontWeight: 600,
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                {content.auth.signup}
              </StyledButton>
              
              <IconButton
                onClick={toggleTheme}
                sx={{
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 'auto',
                  px: { xs: 1, sm: 1.5 }
                }}
              >
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
              
              <Button 
                variant="text"
                onClick={toggleLanguage}
                startIcon={<LanguageIcon />}
                sx={{ 
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 'auto',
                  px: { xs: 1, sm: 2 }
                }}
              >
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {language === 'en' ? 'DE' : 'EN'}
                </Box>
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileMenuOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            bgcolor: 'background.paper',
            borderRight: `1px solid ${theme.palette.divider}`
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Menu
          </Typography>
          <IconButton onClick={handleDrawerToggle}>
            <X size={24} />
          </IconButton>
        </Box>
        <List sx={{ pt: 2 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavClick('features')}
              sx={{
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                  color: 'primary.main'
                }
              }}
            >
              <ListItemText primary="Features" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleNavClick('solutions')}
              sx={{
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                  color: 'primary.main'
                }
              }}
            >
              <ListItemText primary={content.nav.solutions} />
            </ListItemButton>
          </ListItem>
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <MuiLink 
                component={Link}
                to="/documentation"
                onClick={handleDrawerToggle}
                sx={{ 
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'text.secondary',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                <FileText size={16} />
                <Typography 
                  component="span"
                  sx={{ 
                    fontWeight: 400,
                    fontSize: '0.875rem'
                  }}
                >
                  docs
                </Typography>
              </MuiLink>
            </Box>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={() => {
                handleDrawerToggle();
                keycloak.login();
              }}
              sx={{ 
                mb: 2,
                color: 'primary.main', 
                borderColor: 'primary.main',
                fontWeight: 500
              }}
            >
              {content.auth.login}
            </Button>
            <StyledButton 
              variant="contained"
              fullWidth
              onClick={() => {
                handleDrawerToggle();
                if (keycloak && !keycloak.authenticated) {
                  keycloak.login();
                }
              }}
              sx={{ 
                fontWeight: 600
              }}
            >
              {content.auth.signup}
            </StyledButton>
          </Box>
        </List>
      </Drawer>

      <Box sx={{ pt: 8 }}>
        <HeroSection content={content} keycloak={keycloak} />
        
        <Box sx={{ py: 4, bgcolor: 'background.paper' }}>
          <Container maxWidth="lg">
            <Alert 
              icon={<Bot size={20} />}
              severity="info"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: theme.palette.primary.main
                },
                '& .MuiAlert-message': {
                  color: 'text.primary'
                }
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Agentic Features Coming Soon
              </Typography>
              <Typography variant="body2">
                Advanced agentic capabilities and multi-agent orchestration are in development. 
                Build autonomous AI agents that can reason, plan, and execute complex tasks on your on-premise infrastructure.
              </Typography>
            </Alert>
          </Container>
        </Box>
        
        <Box id="features" sx={{ py: 12 }}>
          <Container maxWidth="lg">
            <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 700 }}>
              {content.features.title}
            </Typography>
            <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 8 }}>
              {content.features.subtitle}
            </Typography>
            
            <Grid container spacing={4}>
              {content.features.items.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <FeatureCard>
                    <CardContent sx={{ p: 3 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: 'primary.main',
                          width: 56,
                          height: 56,
                          mb: 3
                        }}
                      >
                        {getIconComponent(feature.iconName, { 
                          size: 30,
                          style: { color: theme.palette.common.black }
                        })}
                      </Avatar>
                      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <SolutionsSection content={content} />

        <Box component="footer" sx={{ py: 8, bgcolor: 'background.elevation1' }}>
          <Container maxWidth="lg">
            <Box sx={{ 
              textAlign: 'center',
              p: 4,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              mb: 4
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Powered by
              </Typography>
              <MuiLink 
                href="https://piosphere.ai"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': { 
                    color: 'primary.dark',
                    textDecoration: 'underline'
                  }
                }}
              >
                piosphere.ai
                <Box component="span" sx={{ ml: 0.5, fontSize: '0.875rem' }}>↗</Box>
              </MuiLink>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Enterprise AI infrastructure platform
              </Typography>
            </Box>
            
            <Box sx={{ mt: 4, pt: 4, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
              <Grid container spacing={2} justifyContent="center">
                {content.footer.legal.links.map((link, index) => (
                  <Grid item key={index}>
                    <MuiLink 
                      sx={{ 
                        cursor: 'pointer',
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main' }
                      }}
                    >
                      {link}
                    </MuiLink>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}

function getIconComponent(iconName, props) {
  const iconMap = {
    zap: Zap,
    brainCircuit: BrainCircuit,
    code: Code,
    rocket: Rocket,
    clock: Clock,
    shoppingBag: ShoppingBag,
    messageCircle: MessageCircle,
    fileText: FileText,
    users: Users,
    bot: Bot,
    search: Search,
    settings: Settings,
    cpu: Cpu,
    cloud: Cloud,
    layers: Layers,
    network: Network,
    database: Database,
    lock: Lock,
    shield: Shield,
    barChart2: BarChart2,
    server: Server,
    monitor: Monitor,
    wifi: Wifi
  };
  
  const IconComponent = iconMap[iconName];
  return IconComponent ? <IconComponent {...props} /> : null;
}